import {
    forwardRef,
    useImperativeHandle,
    useState,
    useRef,
} from 'react'

import TextInput from '../../../../Components/TextInput'
import formatDateDynamic from '../../../../helpers/format-date-dynamic'

const nameValidator = (val) => val.trim().length >= 2

const AcknowledgmentAndAuthorization = forwardRef(({ data, refToPrint }, ref) => {

    const [agreeFinal, setAgreeFinal] = useState(data?.agreeFinal ?? null)
    const [lastFourSSN, setLastFourSSN] = useState(data?.lastFourSSN ?? '')

    const todayDate = formatDateDynamic()
    const [error, setError] = useState('')

    const nameRef = useRef()

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {
            let isValid = true
            const nameData = nameRef.current.validateAndGetData()

            if (!nameData.isValid) {
                setError('Please enter your full legal name')
                isValid = false
            } else if (lastFourSSN.length !== 4) {
                setError('Please enter the last 4 digits of your SSN')
                isValid = false
            } else if (agreeFinal === null) {
                setError('Please select whether you agree or do not agree')
                isValid = false
            } else if (agreeFinal === false) {
                setError('You must agree to proceed')
                isValid = false
            }

            isValid = isValid && nameData.isValid

            if (isValid) {
                setError('')
            }

            return {
                isValid,
                value: {
                    fullName: nameData.value,
                    lastFourSSN: lastFourSSN,
                    date: todayDate,
                    agreeFinal,
                },
            }
        },
    }))

    return (
        <div ref={refToPrint} className="max-w-3xl mx-auto px-4 py-10">
            <div className="flex flex-col w-full space-y-6">
                <h1 className="text-2xl font-bold text-gray-800 text-center">
                    Final Acknowledgment and Authorization
                </h1>

                <div className="space-y-4 text-gray-700 text-sm sm:text-base">
                    <p>
                        I hereby authorize the obtaining of an <strong>"identity verification report"</strong> by
                        the Company at any time after receipt of this authorization and throughout the course of
                        my application or employment, if applicable.
                    </p>

                    <p>
                        I certify that I have read and understand the ESIGN Agreement, the FCRA Disclosure,
                        the Summary of Rights, and the DPPA/GLBA Express Consents.
                    </p>

                    <h2 className="font-semibold text-gray-800 mt-6">Your Electronic Signature</h2>

                    <p>
                        By typing my name and the last four digits of my Social Security Number below, I am
                        authorizing the Company and <strong>VerifiedID LLC (including its authorized agents)</strong> to
                        conduct the identity validation described above.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 items-end">
                        <div>
                            <TextInput
                                title="Full Legal Name"
                                ref={nameRef}
                                data={data?.fullName || ''}
                                placeholder="Enter your full legal name"
                                isMandatory={true}
                                validator={nameValidator}
                                disabled={false}
                            />
                        </div>

                        <div className="my-2">
                            <label className="text-sm text-gray-600 font-medium">Last 4 digits of SSN/National ID Number*</label>
                            <input
                                value={lastFourSSN}
                                onChange={(e) => setLastFourSSN(e.target.value)}
                                maxLength={4}
                                className="mt-1 w-full border border-gray-300 rounded-md px-4 py-2 text-gray-700"
                                placeholder="1234"
                            />
                        </div>

                        <div>
                            <label className="text-sm text-gray-600 font-medium">Date</label>
                            <input
                                value={todayDate}
                                readOnly
                                className="mt-1 w-full border border-gray-300 rounded-md px-4 py-2 bg-gray-100 text-gray-700"
                            />
                        </div>
                    </div>

                    <div className="space-y-2 pt-2">
                        <label className="flex items-start space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="agreeFinal"
                                checked={agreeFinal === true}
                                onChange={() => setAgreeFinal(true)}
                                className="mt-1"
                            />
                            <span className="text-gray-700 text-sm">I AGREE</span>
                        </label>

                        <label className="flex items-start space-x-2 cursor-pointer">
                            <input
                                type="radio"
                                name="agreeFinal"
                                checked={agreeFinal === false}
                                onChange={() => setAgreeFinal(false)}
                                className="mt-1"
                            />
                            <span className="text-gray-700 text-sm">I DO NOT AGREE</span>
                        </label>
                    </div>
                </div>
            </div>

            {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
            )}
        </div>
    )
})

export default AcknowledgmentAndAuthorization
